import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Download, 
  Calendar, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Target,
  Mail,
  Clock,
  BarChart3,
  PieChart,
  FileText,
  Send,
  Settings,
  CheckCircle
} from "lucide-react";

interface ExecutiveReport {
  id: string;
  title: string;
  type: 'financial' | 'operational' | 'strategic' | 'compliance';
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  lastGenerated: string;
  nextScheduled: string;
  recipients: string[];
  status: 'active' | 'paused' | 'draft';
  metrics: ExecutiveMetric[];
}

interface ExecutiveMetric {
  name: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  target?: string;
  status: 'on-track' | 'at-risk' | 'exceeded';
}

interface AutomatedInsight {
  category: 'revenue' | 'efficiency' | 'growth' | 'risk';
  priority: 'high' | 'medium' | 'low';
  title: string;
  insight: string;
  recommendation: string;
  impact: string;
}

export const ExecutiveReports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<string | null>(null);

  const executiveReports: ExecutiveReport[] = [
    {
      id: '1',
      title: 'Monthly Executive Summary',
      type: 'strategic',
      frequency: 'monthly',
      lastGenerated: '2024-01-15T08:00:00Z',
      nextScheduled: '2024-02-15T08:00:00Z',
      recipients: ['ceo@company.com', 'cfo@company.com', 'coo@company.com'],
      status: 'active',
      metrics: [
        { name: 'Revenue Growth', value: '23.4%', change: '+5.2%', trend: 'up', target: '20%', status: 'exceeded' },
        { name: 'Processing Efficiency', value: '96.8%', change: '+2.1%', trend: 'up', target: '95%', status: 'exceeded' },
        { name: 'Customer Satisfaction', value: '4.7/5', change: '+0.3', trend: 'up', target: '4.5/5', status: 'exceeded' },
        { name: 'Cost per Transaction', value: '$0.43', change: '-$0.07', trend: 'up', target: '$0.50', status: 'exceeded' }
      ]
    },
    {
      id: '2',
      title: 'Financial Performance Report',
      type: 'financial',
      frequency: 'weekly',
      lastGenerated: '2024-01-20T06:00:00Z',
      nextScheduled: '2024-01-27T06:00:00Z',
      recipients: ['cfo@company.com', 'finance-team@company.com'],
      status: 'active',
      metrics: [
        { name: 'Weekly Revenue', value: '$127K', change: '+12%', trend: 'up', status: 'on-track' },
        { name: 'Operating Margin', value: '34.2%', change: '+1.8%', trend: 'up', status: 'on-track' },
        { name: 'Cash Flow', value: '$89K', change: '+8%', trend: 'up', status: 'on-track' }
      ]
    },
    {
      id: '3',
      title: 'Operational KPI Dashboard',
      type: 'operational',
      frequency: 'daily',
      lastGenerated: '2024-01-20T07:00:00Z',
      nextScheduled: '2024-01-21T07:00:00Z',
      recipients: ['ops@company.com', 'management@company.com'],
      status: 'active',
      metrics: [
        { name: 'Daily Processing Volume', value: '12,847', change: '+15%', trend: 'up', status: 'exceeded' },
        { name: 'System Uptime', value: '99.97%', change: '0%', trend: 'neutral', status: 'on-track' },
        { name: 'Average Response Time', value: '142ms', change: '-18ms', trend: 'up', status: 'exceeded' }
      ]
    }
  ];

  const automatedInsights: AutomatedInsight[] = [
    {
      category: 'revenue',
      priority: 'high',
      title: 'Revenue Acceleration Detected',
      insight: 'Enterprise segment showing 34% growth rate, significantly outpacing SMB segment at 18%',
      recommendation: 'Allocate additional resources to enterprise sales and customer success teams',
      impact: 'Projected +$2.4M additional revenue over next 6 months'
    },
    {
      category: 'efficiency',
      priority: 'medium',
      title: 'Processing Optimization Opportunity',
      insight: 'AI model accuracy improved to 96.8%, reducing manual review requirements by 23%',
      recommendation: 'Consider reducing manual QA team size or reallocating to value-added tasks',
      impact: 'Potential cost savings of $180K annually'
    },
    {
      category: 'risk',
      priority: 'high',
      title: 'Customer Concentration Risk',
      insight: 'Top 3 customers now represent 47% of total revenue, up from 32% last quarter',
      recommendation: 'Implement customer diversification strategy and strengthen retention programs',
      impact: 'Reduced business risk and improved valuation multiple'
    }
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'financial': return 'default';
      case 'operational': return 'secondary';
      case 'strategic': return 'outline';
      case 'compliance': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'exceeded': return 'text-success';
      case 'on-track': return 'text-primary';
      case 'at-risk': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Executive Reports</h1>
          <p className="text-muted-foreground">
            Automated executive reporting with AI-powered insights and strategic recommendations
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </Button>
          <Button>
            <FileText className="h-4 w-4 mr-2" />
            New Report
          </Button>
        </div>
      </div>

      <Tabs defaultValue="reports" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="reports">Executive Reports</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
          <TabsTrigger value="distribution">Distribution</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {executiveReports.map((report) => (
                <Card key={report.id} className="cursor-pointer hover-lift">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{report.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant={getTypeColor(report.type)}>
                            {report.type.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">
                            {report.frequency.toUpperCase()}
                          </Badge>
                          <Badge variant={report.status === 'active' ? 'default' : 'secondary'}>
                            {report.status.toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Last Generated:</span>
                          <div>{new Date(report.lastGenerated).toLocaleDateString()}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Next Scheduled:</span>
                          <div>{new Date(report.nextScheduled).toLocaleDateString()}</div>
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-muted-foreground mb-2">Key Metrics</div>
                        <div className="grid grid-cols-2 gap-3">
                          {report.metrics.slice(0, 4).map((metric, index) => (
                            <div key={index} className="p-2 bg-muted/30 rounded text-center">
                              <div className="text-sm font-medium">{metric.name}</div>
                              <div className="text-lg font-bold">{metric.value}</div>
                              <div className={`text-xs ${getStatusColor(metric.status)}`}>
                                {metric.change}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-muted-foreground">Recipients: {report.recipients.length}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Report Schedule</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Clock className="h-5 w-5 text-primary" />
                      <div className="flex-1">
                        <div className="font-medium">Daily Operations</div>
                        <div className="text-sm text-muted-foreground">7:00 AM EST</div>
                      </div>
                      <CheckCircle className="h-4 w-4 text-success" />
                    </div>
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Calendar className="h-5 w-5 text-primary" />
                      <div className="flex-1">
                        <div className="font-medium">Weekly Finance</div>
                        <div className="text-sm text-muted-foreground">Monday 6:00 AM</div>
                      </div>
                      <CheckCircle className="h-4 w-4 text-success" />
                    </div>
                    <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/20">
                      <FileText className="h-5 w-5 text-warning" />
                      <div className="flex-1">
                        <div className="font-medium">Monthly Executive</div>
                        <div className="text-sm text-muted-foreground">Feb 15, 8:00 AM</div>
                      </div>
                      <Clock className="h-4 w-4 text-warning" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="insights" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {automatedInsights.map((insight, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{insight.title}</CardTitle>
                    <Badge variant={getPriorityColor(insight.priority)}>
                      {insight.priority.toUpperCase()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-sm mb-1">Key Insight</h4>
                    <p className="text-sm text-muted-foreground">{insight.insight}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-sm mb-1">Recommendation</h4>
                    <p className="text-sm text-muted-foreground">{insight.recommendation}</p>
                  </div>
                  
                  <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
                    <h4 className="font-semibold text-sm mb-1 text-success">Expected Impact</h4>
                    <p className="text-sm text-success">{insight.impact}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="distribution" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <div className="font-semibold mb-2">Executive Team</div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>• CEO: Monthly Strategic Reports</div>
                      <div>• CFO: Weekly Financial + Monthly Strategic</div>
                      <div>• COO: Daily Operations + Weekly Financial</div>
                      <div>• CTO: Daily Operations + Technical Reports</div>
                    </div>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <div className="font-semibold mb-2">Department Heads</div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>• Sales: Revenue reports + Customer insights</div>
                      <div>• Operations: Efficiency metrics + Process reports</div>
                      <div>• Finance: Cost analysis + Budget reports</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Delivery Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 border rounded">
                      <div className="text-2xl font-bold">99.8%</div>
                      <div className="text-sm text-muted-foreground">Delivery Success Rate</div>
                    </div>
                    <div className="text-center p-3 border rounded">
                      <div className="text-2xl font-bold">847</div>
                      <div className="text-sm text-muted-foreground">Reports Sent This Month</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Open Rate</span>
                      <span>94.2%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '94.2%' }} />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Engagement Score</span>
                      <span>8.7/10</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-success h-2 rounded-full" style={{ width: '87%' }} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};